import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import SchoolAuth from "@/pages/SchoolAuth";
import PupilAuth from "@/pages/PupilAuth";
import ParentAuth from "@/pages/ParentAuth";
import PupilHome from "@/pages/pupil/PupilHome";
import TeacherHome from "@/pages/TeacherHome";
import PublicTimetable from "@/pages/PublicTimetable";
import ParentHome from "@/pages/parent/ParentHome";
import PaymentResult from "@/pages/PaymentResult";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ScheduleBuilder from "@/pages/admin/ScheduleBuilder";
import Directory from "@/pages/admin/Directory";
import PupilsAdmin from "@/pages/admin/Pupils";
import NoticesAdmin from "@/pages/admin/Notices";
import SettingsAdmin from "@/pages/admin/Settings";
import BillingAdmin from "@/pages/admin/Billing";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import SuperLogin from "@/pages/super/SuperLogin";
import SuperLayout from "@/pages/super/SuperLayout";
import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperCms from "@/pages/super/SuperCms";
import SuperPlans from "@/pages/super/SuperPlans";
import SuperSchools from "@/pages/super/SuperSchools";
import SuperParents from "@/pages/super/SuperParents";
import SuperVouchers from "@/pages/super/SuperVouchers";
import SuperSettings from "@/pages/super/SuperSettings";
import SuperTeam from "@/pages/super/SuperTeam";
import ResetPassword from "@/pages/ResetPassword";
import KidAuth from "@/pages/KidAuth";
import KidHome from "@/pages/KidHome";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
      <div className="font-display font-black text-2xl animate-pulse">MyTimetable.co.uk…</div>
    </div>
  );
}

function makeRoute(role, redirect) {
  return function Guard({ children }) {
    const { user } = useAuth();
    if (user === null) return <Loading />;
    if (!user || user.role !== role) return <Navigate to={redirect} replace />;
    return children;
  };
}

const SchoolRoute = makeRoute("school", "/school/auth");
const PupilRoute = makeRoute("pupil", "/pupil/auth");
const TeacherRoute = makeRoute("teacher", "/pupil/auth");
const ParentRoute = makeRoute("parent", "/parent/auth");
const SuperRoute = makeRoute("superadmin", "/super/login");
const KidRoute = makeRoute("child", "/kid/auth");

function App() {
  useEffect(() => {
    api.get("/cms/landing").then((r) => {
      const url = r.data?.favicon;
      if (!url) return;
      document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => { l.href = url; });
    }).catch(() => {});
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/school/auth" element={<SchoolAuth />} />
          <Route path="/pupil/auth" element={<PupilAuth />} />
          <Route path="/parent/auth" element={<ParentAuth />} />
          <Route path="/payment/:result" element={<PaymentResult />} />
          <Route path="/t/:token" element={<PublicTimetable />} />
          <Route path="/k/:token" element={<PublicTimetable />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/kid/auth" element={<KidAuth />} />
          <Route path="/kid" element={<KidRoute><KidHome /></KidRoute>} />
          <Route path="/parent" element={<ParentRoute><ParentHome /></ParentRoute>} />
          <Route path="/super/login" element={<SuperLogin />} />
          <Route path="/super" element={<SuperRoute><SuperLayout /></SuperRoute>}>
            <Route index element={<SuperDashboard />} />
            <Route path="cms" element={<SuperCms />} />
            <Route path="plans" element={<SuperPlans />} />
            <Route path="schools" element={<SuperSchools />} />
            <Route path="parents" element={<SuperParents />} />
            <Route path="vouchers" element={<SuperVouchers />} />
            <Route path="settings" element={<SuperSettings />} />
            <Route path="team" element={<SuperTeam />} />
          </Route>
          <Route path="/admin" element={<SchoolRoute><AdminLayout /></SchoolRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="schedule" element={<ScheduleBuilder />} />
            <Route path="directory" element={<Directory />} />
            <Route path="pupils" element={<PupilsAdmin />} />
            <Route path="notices" element={<NoticesAdmin />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="billing" element={<BillingAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
          </Route>
          <Route path="/teacher" element={<TeacherRoute><TeacherHome /></TeacherRoute>} />
          <Route path="/pupil" element={<PupilRoute><PupilHome /></PupilRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}

export default App;
